import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Maps route paths to permission keys
const ROUTE_PERMISSION_MAP = {
  '/medications': 'medications',
  '/talk': 'talk',
  '/amigo': 'ai_companion',
  '/mindpulse': 'mindpulse',
  '/vault': 'private_vault',
  '/doctor-summary': 'doctor_summary',
  '/forums': 'talk', // Forums requires talk permission
};

// Features that normal users can access
const FREE_FEATURES = ['mood', 'appointments', 'store', 'emergency'];

export const ProtectedFeature = ({ children, feature }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    // Admins and superusers have access to everything
    if (user.role === 'admin' || user.role === 'superuser') return;

    // Check if user has permission for this feature
    const permissions = user.permissions || {};
    const hasPermission = permissions[feature] === true;

    if (!hasPermission) {
      toast.error('This feature requires a Pro subscription', {
        action: {
          label: 'View Plans',
          onClick: () => navigate('/pricing')
        }
      });
      navigate('/pricing');
    }
  }, [user, feature, navigate]);

  // If no user yet, show loading or children
  if (!user) return children;

  // Admins and superusers always have access
  if (user.role === 'admin' || user.role === 'superuser') return children;

  // Check permission
  const permissions = user.permissions || {};
  const hasPermission = permissions[feature] === true;

  if (!hasPermission) {
    return null; // Will redirect via useEffect
  }

  return children;
};

// Hook to check if user has access to a feature
export const useFeatureAccess = (feature) => {
  const { user } = useAuth();
  
  if (!user) return { hasAccess: false, isPro: false };
  
  // Admins and superusers have access to everything
  if (user.role === 'admin' || user.role === 'superuser') {
    return { hasAccess: true, isPro: true };
  }
  
  const permissions = user.permissions || {};
  const hasAccess = permissions[feature] === true;
  
  return { hasAccess, isPro: false };
};

// HOC to wrap pages that require specific feature access
export const withFeatureAccess = (WrappedComponent, feature) => {
  return function WithFeatureAccessComponent(props) {
    return (
      <ProtectedFeature feature={feature}>
        <WrappedComponent {...props} />
      </ProtectedFeature>
    );
  };
};
